import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  Clock,
  Link as LinkIcon,
  Unlink as UnlinkIcon,
  ChevronDown,
  ChevronRight,
  Search,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  X,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Worksection linker section embedded on /unified-projects/:id (SalesProjectEditPage).
 *
 * Two states:
 *   1. No project link → typeahead picker with fuzzy suggestions
 *   2. Linked → WS project header + list of WS tasks with per-task linker to portal tasks
 *
 * Many-to-many at task level: one portal task can be linked to many WS tasks
 * (user's case: 1 "website" portal task = 5-6 WS tasks for FE/BE/QA/design).
 *
 * Phase 2 — ONLY records mappings in separate ws_*_links collections.
 * No writes to db.tasks / db.projects. Phase 3 will add Worksection cost to
 * portal tasks behind a global toggle (default off).
 */
const WorksectionProjectSection = ({ projectId, portalTasks, canEdit }) => {
  // Collapsed by default — section takes vertical space, user can expand when needed.
  // Header always shows the linked/not-linked badge + counts so status is visible at a glance.
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [projectLink, setProjectLink] = useState(null);
  const [otherLinks, setOtherLinks] = useState([]); // when M:N (rare)
  const [suggestions, setSuggestions] = useState([]);
  const [manualSearch, setManualSearch] = useState("");
  const [allWsProjects, setAllWsProjects] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // For linked-state: WS tasks of the linked project + existing task links
  const [wsTasks, setWsTasks] = useState([]);
  const [taskLinks, setTaskLinks] = useState([]); // bulk for project
  const [tasksLoading, setTasksLoading] = useState(false);

  // --------- fetchers ---------

  const loadProjectLink = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await api.get(`/worksection/project-links/portal/${projectId}`);
      if (res.data && res.data.id) {
        setProjectLink(res.data);
        setOtherLinks([]);
      } else if (res.data && Array.isArray(res.data.links) && res.data.links.length > 0) {
        // Multiple (M:N case) — show first as primary, list others as info
        setProjectLink(res.data.links[0]);
        setOtherLinks(res.data.links.slice(1));
      } else {
        setProjectLink(null);
        setOtherLinks([]);
      }
    } catch (e) {
      console.warn("worksection project link fetch failed:", e?.response?.status);
      setProjectLink(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const loadSuggestions = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get(`/worksection/project-links/portal/${projectId}/suggestions?limit=5`);
      setSuggestions(res.data || []);
    } catch (e) {
      console.warn("WS suggestions failed:", e?.response?.status);
    }
  }, [projectId]);

  const loadAllWsProjects = useCallback(async () => {
    try {
      // Show both active AND pending — WS projects often start in `pending` until
      // they're officially kicked off, and PMs need to link them too.
      const res = await api.get("/worksection/projects?statuses=active,pending");
      setAllWsProjects(res.data || []);
    } catch (e) {
      console.warn("WS projects list failed:", e?.response?.status);
    }
  }, []);

  // On-demand pull of the WS project list so a freshly-created Worksection
  // project shows up immediately instead of waiting for the daily 04:00 sync.
  const refreshWsProjects = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await api.post("/worksection/projects/refresh");
      await Promise.all([loadAllWsProjects(), loadSuggestions()]);
      const total = res?.data?.projects?.total;
      toast.success(total != null ? `Synced ${total} Worksection projects` : "Worksection projects refreshed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not refresh from Worksection");
    } finally {
      setRefreshing(false);
    }
  }, [loadAllWsProjects, loadSuggestions]);

  const loadWsTasksAndLinks = useCallback(async () => {
    if (!projectLink) return;
    setTasksLoading(true);
    try {
      const [tasksRes, linksRes] = await Promise.all([
        api.get(`/worksection/tasks?ws_project_id=${projectLink.ws_project_id}`),
        api.get(`/worksection/task-links/portal-project/${projectId}`),
      ]);
      setWsTasks(tasksRes.data || []);
      setTaskLinks(linksRes.data || []);
    } catch (e) {
      console.warn("WS tasks/links load failed:", e?.response?.status);
    } finally {
      setTasksLoading(false);
    }
  }, [projectLink, projectId]);

  // --------- effects ---------

  useEffect(() => {
    loadProjectLink();
  }, [loadProjectLink]);

  useEffect(() => {
    if (!projectLink && expanded) {
      loadSuggestions();
      loadAllWsProjects();
    }
  }, [projectLink, expanded, loadSuggestions, loadAllWsProjects]);

  useEffect(() => {
    // Load WS tasks + links as soon as we know the project IS linked, even if the
    // section is collapsed — so the header badge can show honest "N/M tasks" counts
    // ("Linked · 5/6 tasks") instead of misleading "0/0 tasks".
    // The bulk endpoints are cheap (one round-trip each) and only fire when there's
    // actually a project link.
    if (projectLink) {
      loadWsTasksAndLinks();
    }
  }, [projectLink, loadWsTasksAndLinks]);

  // --------- actions ---------

  const handleLinkProject = async (wsProjectId, wsProjectName, confidence = null) => {
    if (!confidence || confidence >= 0.5 || window.confirm(
      `Suggestion confidence is low (${Math.round((confidence || 0) * 100)}%). Link this portal project to "${wsProjectName}" anyway?`
    )) {
      setLinking(true);
      try {
        await api.post("/worksection/project-links", {
          portal_project_id: projectId,
          ws_project_id: wsProjectId,
        });
        toast.success(`Linked to Worksection: ${wsProjectName}`);
        setPickerOpen(false);
        setManualSearch("");
        await loadProjectLink();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to link");
      } finally {
        setLinking(false);
      }
    }
  };

  const handleUnlinkProject = async () => {
    if (!projectLink) return;
    if (!window.confirm(
      `Unlink "${projectLink.ws_project_name}"? This will also remove ${taskLinks.length} task-level link(s). Worksection data itself is not deleted.`
    )) return;
    try {
      const res = await api.delete(`/worksection/project-links/${projectLink.id}`);
      const cascaded = res.data?.cascaded_task_links_deleted || 0;
      toast.success(`Unlinked${cascaded ? ` (removed ${cascaded} task link(s))` : ""}`);
      setProjectLink(null);
      setOtherLinks([]);
      setTaskLinks([]);
      setWsTasks([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed");
    }
  };

  const handleLinkTask = async (wsTaskId, portalTaskId, portalTaskName, wsTaskName) => {
    try {
      await api.post("/worksection/task-links", {
        portal_task_id: portalTaskId,
        ws_task_id: wsTaskId,
      });
      toast.success(`Linked WS "${wsTaskName.slice(0, 30)}" → "${portalTaskName.slice(0, 30)}"`);
      await loadWsTasksAndLinks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Task link failed");
    }
  };

  const handleUnlinkTask = async (linkId) => {
    try {
      await api.delete(`/worksection/task-links/${linkId}`);
      toast.success("Task unlinked");
      await loadWsTasksAndLinks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed");
    }
  };

  // --------- derived ---------

  const filteredWsProjects = useMemo(() => {
    const q = manualSearch.trim().toLowerCase();
    const linked = new Set(); // exclude self-link suggestion duplicates
    if (projectLink) linked.add(projectLink.ws_project_id);
    if (!q) return allWsProjects.filter(p => !linked.has(p.ws_project_id)).slice(0, 50);
    return allWsProjects
      .filter(p => !linked.has(p.ws_project_id) && (p.ws_name || "").toLowerCase().includes(q))
      .slice(0, 30);
  }, [allWsProjects, manualSearch, projectLink]);

  const taskLinksByWsTaskId = useMemo(() => {
    const m = new Map();
    taskLinks.forEach((l) => {
      if (!m.has(l.ws_task_id)) m.set(l.ws_task_id, []);
      m.get(l.ws_task_id).push(l);
    });
    return m;
  }, [taskLinks]);

  const linkedCount = taskLinks.length;
  const linkedWsCount = useMemo(() => new Set(taskLinks.map(l => l.ws_task_id)).size, [taskLinks]);

  // --------- render ---------

  if (loading) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
            <Clock className="h-4 w-4 text-emerald-400" />
            Worksection
            <span className="text-xs text-zinc-500">loading…</span>
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 text-zinc-50 text-base font-semibold hover:text-emerald-300 transition-colors"
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Clock className="h-4 w-4 text-emerald-400" />
          Worksection
          {projectLink ? (
            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px] uppercase tracking-wide ml-1">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Linked · {linkedWsCount}/{wsTasks.length} tasks
            </Badge>
          ) : (
            <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 text-[10px] uppercase tracking-wide ml-1">
              Not linked
            </Badge>
          )}
        </button>
        {projectLink && canEdit && expanded && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleUnlinkProject}
            className="text-zinc-500 hover:text-red-400 h-7 text-xs"
          >
            <UnlinkIcon className="h-3.5 w-3.5 mr-1" />
            Unlink project
          </Button>
        )}
      </CardHeader>

      {!expanded ? null : (
        <CardContent className="pt-0">
          {/* === NOT LINKED — picker === */}
          {!projectLink && (
            <div className="space-y-3">
              <p className="text-xs text-zinc-400">
                Connect this portal project to a Worksection project to track time spent by your team
                (designers, developers, testers). After linking, hourly cost from Worksection will be
                visible per portal task <span className="italic">(read-only in Phase 2)</span>.
              </p>

              {/* Fuzzy suggestions */}
              {suggestions.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[11px] uppercase tracking-wide text-zinc-500">
                    Suggested matches (by name similarity)
                  </div>
                  {suggestions.map((s) => (
                    <SuggestionRow
                      key={s.ws_project_id}
                      suggestion={s}
                      onLink={() => handleLinkProject(s.ws_project_id, s.ws_name, s.score)}
                      disabled={linking || !canEdit}
                    />
                  ))}
                </div>
              )}

              {/* Manual picker */}
              {canEdit && (
                <div className="space-y-2 pt-2 border-t border-zinc-800">
                  <div className="text-[11px] uppercase tracking-wide text-zinc-500">
                    Or pick manually
                  </div>
                  <button
                    type="button"
                    onClick={() => setPickerOpen((v) => !v)}
                    className="w-full flex items-center justify-between bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-400 hover:border-zinc-600"
                  >
                    <span className="flex items-center gap-2">
                      <Search className="h-3.5 w-3.5" />
                      Search active Worksection projects…
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  {pickerOpen && (
                    <div className="bg-zinc-950 border border-zinc-800 rounded">
                      <div className="p-2 border-b border-zinc-800 flex items-center gap-2">
                        <Input
                          autoFocus
                          placeholder="Type to filter…"
                          value={manualSearch}
                          onChange={(e) => setManualSearch(e.target.value)}
                          className="bg-zinc-900 border-zinc-700 text-sm flex-1"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={refreshWsProjects}
                          disabled={refreshing}
                          title="Pull the latest projects from Worksection (use if a new project is missing)"
                          className="text-zinc-400 hover:text-emerald-300 h-8 px-2 shrink-0"
                        >
                          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
                        </Button>
                      </div>
                      {manualSearch.trim() && filteredWsProjects.length === 0 && (
                        <div className="px-3 py-2 text-[11px] text-zinc-500 italic border-b border-zinc-800/60">
                          Missing a project? Click the refresh icon to re-sync from Worksection.
                        </div>
                      )}
                      <div className="max-h-64 overflow-y-auto">
                        {filteredWsProjects.length === 0 ? (
                          <div className="p-3 text-xs text-zinc-500 italic">No matches</div>
                        ) : (
                          filteredWsProjects.map((p) => (
                            <button
                              key={p.ws_project_id}
                              type="button"
                              onClick={() => handleLinkProject(p.ws_project_id, p.ws_name, null)}
                              disabled={linking}
                              className="w-full text-left px-3 py-2 hover:bg-zinc-800 text-sm border-b border-zinc-800/60 last:border-0"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-zinc-200 truncate flex-1">{p.ws_name || `Project ${p.ws_project_id}`}</span>
                                {p.ws_status === "pending" && (
                                  <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[9px] uppercase shrink-0">
                                    pending
                                  </Badge>
                                )}
                                {p.ws_status === "active" && (
                                  <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[9px] uppercase shrink-0">
                                    active
                                  </Badge>
                                )}
                              </div>
                              <div className="text-[11px] text-zinc-500">{p.ws_company || ""} · #{p.ws_project_id}</div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* === LINKED — show WS project + tasks linker === */}
          {projectLink && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-400">
                Linked to <span className="text-zinc-200 font-medium">"{projectLink.ws_project_name}"</span>
                {projectLink.ws_current?.last_synced_at && (
                  <span className="text-zinc-500"> · synced {timeAgo(projectLink.ws_current.last_synced_at)}</span>
                )}
              </div>
              {otherLinks.length > 0 && (
                <div className="text-[11px] text-amber-400">
                  ⚠ This portal project is linked to {otherLinks.length + 1} WS projects (M:N). Other links:{" "}
                  {otherLinks.map((l) => l.ws_project_name).join(", ")}
                </div>
              )}

              {tasksLoading ? (
                <div className="text-xs text-zinc-500 italic">Loading Worksection tasks…</div>
              ) : wsTasks.length === 0 ? (
                <div className="text-xs text-zinc-500 italic">No tasks in this Worksection project yet</div>
              ) : (
                <WsTasksLinker
                  wsTasks={wsTasks}
                  taskLinksByWsTaskId={taskLinksByWsTaskId}
                  portalTasks={portalTasks || []}
                  canEdit={canEdit}
                  onLink={handleLinkTask}
                  onUnlink={handleUnlinkTask}
                />
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// =========================================================================
// SubComponent: suggestion row with confidence indicator
// =========================================================================

const SuggestionRow = ({ suggestion, onLink, disabled }) => {
  const conf = suggestion.score || 0;
  const tone =
    conf >= 0.6 ? "emerald" :
      conf >= 0.4 ? "cyan" :
        conf >= 0.25 ? "amber" : "zinc";
  const toneClass = {
    emerald: "border-emerald-500/30 text-emerald-300 bg-emerald-500/5",
    cyan: "border-cyan-500/30 text-cyan-300 bg-cyan-500/5",
    amber: "border-amber-500/30 text-amber-300 bg-amber-500/5",
    zinc: "border-zinc-700 text-zinc-400 bg-zinc-900/40",
  }[tone];
  return (
    <div className={`flex items-center justify-between px-3 py-2 border rounded ${toneClass}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-zinc-200 truncate">{suggestion.ws_name}</div>
        <div className="text-[11px] text-zinc-500">
          {suggestion.ws_company || ""} · confidence {Math.round(conf * 100)}%
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onLink}
        disabled={disabled}
        className="border-zinc-700 h-7 text-xs ml-2"
      >
        <LinkIcon className="h-3.5 w-3.5 mr-1" />
        Link
      </Button>
    </div>
  );
};

// =========================================================================
// SubComponent: WS tasks table with per-task portal-task picker (M:N)
// =========================================================================

const WsTasksLinker = ({ wsTasks, taskLinksByWsTaskId, portalTasks, canEdit, onLink, onUnlink }) => {
  return (
    <div className="border border-zinc-800 rounded overflow-hidden">
      <div className="px-3 py-1.5 bg-zinc-950 border-b border-zinc-800 text-[11px] uppercase tracking-wide text-zinc-500 flex items-center justify-between">
        <span>Worksection tasks ({wsTasks.length})</span>
        <span className="text-zinc-600">tip: many WS tasks can map to ONE portal task</span>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {wsTasks.map((wt) => {
          const links = taskLinksByWsTaskId.get(wt.ws_task_id) || [];
          return (
            <WsTaskRow
              key={wt.ws_task_id}
              wsTask={wt}
              links={links}
              portalTasks={portalTasks}
              canEdit={canEdit}
              onLink={onLink}
              onUnlink={onUnlink}
            />
          );
        })}
      </div>
    </div>
  );
};

const WsTaskRow = ({ wsTask, links, portalTasks, canEdit, onLink, onUnlink }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const pickerRef = React.useRef(null);

  // When picker opens on a row near the bottom of the scrollable list,
  // the dropdown gets clipped by the `max-h-96 overflow-y-auto` container.
  // Scroll it into view so user can actually see + interact with the search input.
  useEffect(() => {
    if (pickerOpen && pickerRef.current) {
      // Delay one tick so the picker has actually rendered before we measure.
      const t = setTimeout(() => {
        pickerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 30);
      return () => clearTimeout(t);
    }
  }, [pickerOpen]);

  const linkedToIds = useMemo(() => new Set(links.map((l) => l.portal_task_id)), [links]);
  const filtered = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    return portalTasks
      .filter((t) => !linkedToIds.has(t.id) && (!q || (t.name || "").toLowerCase().includes(q)))
      .slice(0, 30);
  }, [portalTasks, linkedToIds, pickerSearch]);

  const isClosed = wsTask.ws_status === "done";

  return (
    <div className="px-3 py-2 border-b border-zinc-800/60 last:border-0 hover:bg-zinc-900/30">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-200 truncate">{wsTask.ws_name || `Task ${wsTask.ws_task_id}`}</span>
            <Badge className={`text-[10px] ${isClosed
              ? "bg-zinc-700/40 text-zinc-400 border-zinc-700"
              : "bg-cyan-500/15 text-cyan-300 border-cyan-500/30"
            }`}>{wsTask.ws_status || "?"}</Badge>
          </div>
          {links.length > 0 ? (
            <div className="mt-1 space-y-0.5">
              {links.map((l) => {
                const pt = portalTasks.find((t) => t.id === l.portal_task_id);
                return (
                  <div key={l.id} className="flex items-center gap-2 text-[11px]">
                    <LinkIcon className="h-3 w-3 text-emerald-400 shrink-0" />
                    <span className="text-emerald-300 truncate flex-1">
                      → {pt?.name || `(portal task ${l.portal_task_id.slice(0, 8)}…)`}
                    </span>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => onUnlink(l.id)}
                        className="text-zinc-500 hover:text-red-400"
                        title="Unlink"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-[11px] text-zinc-500 mt-0.5">Not linked to any portal task</div>
          )}
        </div>
        {canEdit && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPickerOpen((v) => !v)}
            className="text-zinc-400 hover:text-emerald-300 h-6 text-[11px] shrink-0"
          >
            <LinkIcon className="h-3 w-3 mr-1" />
            {links.length === 0 ? "Link" : "+ Add"}
          </Button>
        )}
      </div>

      {pickerOpen && canEdit && (
        <div ref={pickerRef} className="mt-2 bg-zinc-950 border border-zinc-700 rounded">
          <div className="p-2 border-b border-zinc-800">
            <Input
              autoFocus
              placeholder="Search portal tasks of this project…"
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              className="bg-zinc-900 border-zinc-700 text-xs h-7"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-2 text-[11px] text-zinc-500 italic">
                {portalTasks.length === 0
                  ? "No portal tasks in this project yet"
                  : linkedToIds.size === portalTasks.length
                    ? "All portal tasks already linked to this WS task"
                    : "No matches"}
              </div>
            ) : (
              filtered.map((pt) => (
                <button
                  key={pt.id}
                  type="button"
                  onClick={() => {
                    onLink(wsTask.ws_task_id, pt.id, pt.name, wsTask.ws_name);
                    setPickerOpen(false);
                    setPickerSearch("");
                  }}
                  className="w-full text-left px-2 py-1.5 hover:bg-zinc-800 text-[12px] border-b border-zinc-800/60 last:border-0"
                >
                  <span className="text-zinc-200">{pt.name || "(unnamed)"}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =========================================================================
// Helpers
// =========================================================================

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.floor((now - then) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export default WorksectionProjectSection;
